import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { adminAPI } from '../api/api';
import { Building2, Search, Plus, Trash2, ExternalLink, ChevronDown, Check, Pause, Play, Users as UsersIcon } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Card, Btn, PlanBadge, StatusDot, Modal, TextField, SelectField, formatDate, Spinner } from '../components/admin/AdminUI';

const PLAN_OPTIONS = [
  { code: 'starter',    label: 'Starter' },
  { code: 'pro',        label: 'Pro' },
  { code: 'team',       label: 'Team' },
  { code: 'enterprise', label: 'Enterprise' },
];

const PlanSelector = ({ orgId, currentPlanCode, onPlanChanged }) => {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  const handleSelect = async (planCode) => {
    if (planCode === currentPlanCode) return setOpen(false);
    setUpdating(true);
    try {
      await adminAPI.updateOrgPlan(orgId, planCode);
      toast({ title: 'Plan modifié', description: `Plan changé vers ${planCode}` });
      onPlanChanged();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally { setUpdating(false); setOpen(false); }
  };

  return (
    <div className="relative">
      <Btn tone="subtle" onClick={() => setOpen(!open)} disabled={updating}>
        {updating ? '…' : 'Plan'} <ChevronDown className="h-3 w-3" />
      </Btn>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-50 w-44 bg-[#0d0d24] border border-[rgba(108,71,255,.3)] rounded-lg shadow-xl py-1">
            {PLAN_OPTIONS.map((opt) => (
              <button key={opt.code} onClick={() => handleSelect(opt.code)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-300 hover:bg-[rgba(108,71,255,.1)]">
                <span>{opt.label}</span>
                {currentPlanCode === opt.code && <Check className="h-3.5 w-3.5 text-emerald-400" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const AdminOrgs = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', slug: '', plan_code: 'starter', owner_user_id: '' });
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { document.title = "Skilldeck — Admin · Organisations"; }, []);
  useEffect(() => { const t = setTimeout(() => setSearchDebounced(search), 250); return () => clearTimeout(t); }, [search]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminAPI.listOrganizations(searchDebounced || undefined);
      setOrgs(data);
    } catch { toast({ title: 'Erreur', description: 'Impossible de charger les organisations', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [searchDebounced, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault(); setCreating(true);
    try {
      await adminAPI.createOrg({
        name: createForm.name, slug: createForm.slug, plan_code: createForm.plan_code,
        ...(createForm.owner_user_id ? { owner_user_id: createForm.owner_user_id } : {}),
      });
      toast({ title: 'Organisation créée', description: `"${createForm.name}" créée` });
      setShowCreate(false);
      setCreateForm({ name: '', slug: '', plan_code: 'starter', owner_user_id: '' });
      fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
    finally { setCreating(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleting(true);
    try {
      await adminAPI.deleteOrg(deleteTarget.id);
      toast({ title: 'Organisation supprimée', description: `"${deleteTarget.name}" supprimée` });
      setDeleteTarget(null); fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
    finally { setDeleting(false); }
  };

  const toggleStatus = async (org) => {
    const next = org.status === 'active' ? 'suspended' : 'active';
    try {
      await adminAPI.setOrgStatus(org.id, next);
      toast({ title: next === 'suspended' ? 'Organisation suspendue' : 'Organisation réactivée', description: org.name });
      fetchData();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
  };

  const filtered = orgs.filter((o) => {
    const code = o.subscription?.plan_code || o.plan_code;
    if (planFilter !== 'all' && code !== planFilter) return false;
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    return true;
  });

  return (
    <AdminLayout title="Organisations">
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">

        <Card padding="p-0">
          <div className="px-5 py-4 border-b border-[rgba(108,71,255,.12)] flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (nom, slug)…"
                className="w-full pl-9 pr-4 py-2 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/50" />
            </div>
            <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
              className="px-3 py-2 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-xs text-white">
              <option value="all">Tous les plans</option>
              {PLAN_OPTIONS.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-xs text-white">
              <option value="all">Tous statuts</option>
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
              <option value="cancelled">Annulé</option>
            </select>
            <div className="ml-auto"><Btn onClick={() => setShowCreate(true)}><Plus className="h-3 w-3" /> Nouvelle org</Btn></div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p>Aucune organisation</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(108,71,255,.12)] text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    <th className="px-5 py-3">Organisation</th>
                    <th className="px-5 py-3">Plan</th>
                    <th className="px-5 py-3">Membres</th>
                    <th className="px-5 py-3">Statut</th>
                    <th className="px-5 py-3">Créé le</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(108,71,255,.06)]">
                  {filtered.map((org) => {
                    const sub = org.subscription;
                    const planCode = sub?.plan_code || org.plan_code;
                    return (
                      <tr key={org.id} className="hover:bg-[rgba(108,71,255,.04)] transition-colors">
                        <td className="px-5 py-3">
                          <p className="text-sm font-medium text-white">{org.name}</p>
                          <p className="text-xs text-gray-500 font-mono">{org.slug}</p>
                        </td>
                        <td className="px-5 py-3"><PlanBadge code={planCode} name={sub?.plan_name} /></td>
                        <td className="px-5 py-3">
                          <span className="text-sm text-gray-300 inline-flex items-center gap-1.5">
                            <UsersIcon className="h-3.5 w-3.5 text-gray-500" /> {org.member_count}
                          </span>
                        </td>
                        <td className="px-5 py-3"><StatusDot status={org.status} /></td>
                        <td className="px-5 py-3 text-sm text-gray-400">{formatDate(org.created_at)}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <Btn tone="subtle" onClick={() => navigate(`/admin/orgs/${org.id}`)} title="Détails"><ExternalLink className="h-3 w-3" /></Btn>
                            <PlanSelector orgId={org.id} currentPlanCode={planCode} onPlanChanged={fetchData} />
                            <Btn tone="subtle" onClick={() => toggleStatus(org)} title={org.status === 'active' ? 'Suspendre' : 'Réactiver'}>
                              {org.status === 'active' ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                            </Btn>
                            <Btn tone="danger" onClick={() => setDeleteTarget({ id: org.id, name: org.name })} title="Supprimer"><Trash2 className="h-3 w-3" /></Btn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Create modal */}
        <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Créer une organisation">
          <form onSubmit={handleCreate} className="px-6 py-5 space-y-4">
            <TextField label="Nom *" required value={createForm.name} onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme Corp" />
            <TextField label="Slug *" hint="(minuscules, chiffres, tirets)" required value={createForm.slug}
              onChange={(e) => setCreateForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} placeholder="acme-corp" />
            <SelectField label="Plan" value={createForm.plan_code} onChange={(e) => setCreateForm(f => ({ ...f, plan_code: e.target.value }))}>
              {PLAN_OPTIONS.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
            </SelectField>
            <TextField label="UUID propriétaire" hint="(optionnel)" value={createForm.owner_user_id}
              onChange={(e) => setCreateForm(f => ({ ...f, owner_user_id: e.target.value }))}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            <div className="flex justify-end gap-3 pt-2">
              <Btn type="button" tone="ghost" onClick={() => setShowCreate(false)}>Annuler</Btn>
              <Btn type="submit" disabled={creating}>{creating ? 'Création…' : 'Créer'}</Btn>
            </div>
          </form>
        </Modal>

        {/* Delete confirm */}
        <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Supprimer l'organisation" danger maxWidth="max-w-sm">
          <div className="px-6 py-5">
            <p className="text-sm text-gray-300 mb-5">
              Supprimer <span className="font-semibold text-white">"{deleteTarget?.name}"</span> supprimera définitivement
              tous ses membres, consultants, dossiers et abonnements.
            </p>
            <div className="flex gap-3">
              <Btn tone="ghost" className="flex-1 justify-center" onClick={() => setDeleteTarget(null)}>Annuler</Btn>
              <Btn tone="danger" className="flex-1 justify-center" onClick={handleDelete} disabled={deleting}>{deleting ? 'Suppression…' : 'Supprimer'}</Btn>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminOrgs;
